import React, { useEffect, useState, useCallback } from "react";
import { Sliders, RotateCcw, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

/**
 * ScannerTuningCard — live-tunable scanner filters for the vetting cockpit.
 *
 * Backed by GET/PATCH /api/admin/scanner/config. Changes take effect on the
 * NEXT scanner tick (30s in-memory cache backend-side). No redeploy needed.
 *
 * Knobs:
 *   • Cap per cycle (1-20)      — how many alerts can hit the queue each scan
 *   • Min confidence (0-1)      — hard gate on strategy confidence score
 *   • Moonshot min grade (S/A/B/off) — filter B moonshots or drop the gate entirely
 *
 * Debounces PATCH calls so slider-dragging doesn't spam the API.
 */
const GRADE_OPTIONS = [
  { value: "S", label: "S only" },
  { value: "A", label: "A + S" },
  { value: "B", label: "B + A + S" },
  { value: null, label: "Off" },
];

const DEFAULTS = { cap_per_cycle: 4, min_confidence: 0.75, moonshot_min_grade: "A" };

export default function ScannerTuningCard() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/scanner/config")
      .then((r) => {
        if (!alive) return;
        setCfg(r.data.config);
        setInitial(r.data.config);
      })
      .catch(() => alive && toast.error("Couldn't load scanner config"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const patch = useCallback(async (body) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/scanner/config", body);
      setCfg(r.data.config);
      setInitial(r.data.config);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  }, []);

  // Debounce slider drag → PATCH so we don't spam the API on every pixel.
  useEffect(() => {
    if (!cfg || !initial || !dirty) return;
    const t = setTimeout(() => {
      const body = {};
      if (cfg.cap_per_cycle !== initial.cap_per_cycle) body.cap_per_cycle = cfg.cap_per_cycle;
      if (cfg.min_confidence !== initial.min_confidence) body.min_confidence = cfg.min_confidence;
      if (cfg.moonshot_min_grade !== initial.moonshot_min_grade) body.moonshot_min_grade = cfg.moonshot_min_grade;
      if (Object.keys(body).length) patch(body);
    }, 400);
    return () => clearTimeout(t);
  }, [cfg, initial, dirty, patch]);

  const setField = (k, v) => {
    setCfg((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  const resetDefaults = () => {
    setCfg({ ...cfg, ...DEFAULTS });
    setDirty(true);
  };

  if (loading || !cfg) {
    return (
      <div className="bg-white border border-mb-border rounded-lg p-4 flex items-center gap-2 text-sm text-neutral-500" data-testid="scanner-tuning-card-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading scanner filters…
      </div>
    );
  }

  const confPct = Math.round(cfg.min_confidence * 100);

  return (
    <div className="bg-white border border-mb-border rounded-lg p-5" data-testid="scanner-tuning-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-2">
          <Sliders className="w-4 h-4 text-mb-emerald mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Scanner filters</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Tighter = fewer, stronger candidates. Changes hit the next scan tick.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />}
          <button
            type="button"
            onClick={resetDefaults}
            className="flex items-center gap-1 text-[11px] text-mb-emerald hover:underline"
            data-testid="scanner-tuning-reset"
          >
            <RotateCcw className="w-3 h-3" /> Reset (Option A)
          </button>
        </div>
      </div>

      {/* Cap per cycle */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-mono-data tracking-wide text-neutral-600 uppercase">Cap per scan cycle</label>
          <span className="font-mono-data text-base font-bold text-neutral-900 tabular-nums" data-testid="scanner-tuning-cap-value">
            {cfg.cap_per_cycle}
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={12}
          step={1}
          value={cfg.cap_per_cycle}
          onChange={(e) => setField("cap_per_cycle", parseInt(e.target.value, 10))}
          className="w-full accent-mb-emerald"
          data-testid="scanner-tuning-cap-slider"
        />
        <div className="flex justify-between text-[10px] font-mono-data text-neutral-400 mt-1">
          <span>1</span><span>3</span><span>6</span><span>9</span><span>12</span>
        </div>
      </div>

      {/* Confidence gate */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-mono-data tracking-wide text-neutral-600 uppercase">Min confidence</label>
          <span className="font-mono-data text-base font-bold text-neutral-900 tabular-nums" data-testid="scanner-tuning-conf-value">
            {confPct}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={95}
          step={5}
          value={confPct}
          onChange={(e) => setField("min_confidence", parseInt(e.target.value, 10) / 100)}
          className="w-full accent-mb-emerald"
          data-testid="scanner-tuning-conf-slider"
        />
        <div className="flex justify-between text-[10px] font-mono-data text-neutral-400 mt-1">
          <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>95%</span>
        </div>
      </div>

      {/* Moonshot grade floor */}
      <div>
        <label className="text-[11px] font-mono-data tracking-wide text-neutral-600 uppercase mb-1.5 block">Moonshot grade floor</label>
        <div className="grid grid-cols-4 gap-1.5" data-testid="scanner-tuning-grade-group">
          {GRADE_OPTIONS.map((opt) => {
            const active = cfg.moonshot_min_grade === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setField("moonshot_min_grade", opt.value)}
                className={`px-2 py-2 rounded-md text-[11px] font-mono-data font-semibold border transition
                  ${active
                    ? "bg-mb-emerald text-white border-mb-emerald"
                    : "bg-white text-neutral-700 border-neutral-300 hover:border-mb-emerald"}`}
                data-testid={`scanner-tuning-grade-${opt.value || "off"}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Small-cap expansion off switch */}
      <div className="mt-4 flex items-start justify-between gap-3 pt-4 border-t border-mb-border">
        <div>
          <div className="text-[11px] font-mono-data tracking-wide text-neutral-600 uppercase">Small-cap expansion</div>
          <p className="text-[11px] text-neutral-500 mt-0.5">Also scan every stock between the market-cap floor and $3B, ranked as small caps.</p>
        </div>
        <Switch
          checked={!!cfg.smallcap_expansion}
          disabled={saving}
          onCheckedChange={(v) => { setCfg((p) => ({ ...p, smallcap_expansion: v })); patch({ smallcap_expansion: v }); }}
          data-testid="scanner-tuning-smallcap-switch"
        />
      </div>
    </div>
  );
}
